import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { endOfDay, format, isSameDay, startOfDay } from 'date-fns';
import { enUS, it as itLocale, zhCN } from 'date-fns/locale';
import type { Report } from '../../store/useReportStore';
import { useChatStore } from '../../store/useChatStore';
import { useMoodStore } from '../../store/useMoodStore';
import { useAuthStore } from '../../store/useAuthStore';
import { useTodoStore } from '../../store/useTodoStore';
import { useGrowthStore } from '../../store/useGrowthStore';
import { resolveCachedPlantForDate, usePlantStore } from '../../store/usePlantStore';
import { useReportStore } from '../../store/useReportStore';
import type { DailyPlantRecord } from '../../types/plant';
import type { MoodDistributionItem } from './reportPageHelpers';
import type { ActivityDistributionItem } from './reportPageHelpers';
import { getDailyActivityDistribution, getDailyMoodDistribution, getMessagesForReport } from './reportPageHelpers';
import { callPlantGenerateAPI } from '../../api/client';
import { getMoodDisplayLabel } from '../../lib/moodOptions';
import { getPlantDisplayName } from '../../lib/plantDisplayName';
import { canEditDailyMyDiary } from '../../lib/reportDiaryEditPolicy';
import { APP_GREEN_GLASS_BUTTON_STYLE } from '../../lib/modalTheme';
import { computeDailyTodoStats, generateActionSummary, generateMoodSummary } from '../../store/reportHelpers';
import { buildDiaryPageSnapshot, type DiarySnapshotLang } from '../../store/reportDiarySnapshot';
import { PlantImage } from './plant/PlantImage';
import growthStarImage from '../../assets/growth/growth-star.png';
import {
  DonutChart, type DataItem,
  ACTIVITY_I18N_KEYS, ACTIVITY_UI_COLORS, MOOD_UI_COLORS,
} from './DiaryDonutChart';
import { ReportDetailPageHeader } from './ReportDetailPageHeader';
import { DIARY_COPY } from './diaryBookViewerData';
import {
  getReportCompanionName,
  resolveReportObservationText,
} from './reportObservation';

const DIARY_DONUT_SCALE = 0.9;
const DIARY_DONUT_SIZE = 110 * DIARY_DONUT_SCALE;
const DIARY_DONUT_INNER_RADIUS = 20 * DIARY_DONUT_SCALE;
const DIARY_DONUT_OUTER_RADIUS = 44 * DIARY_DONUT_SCALE;
const DIARY_DONUT_FONT_SIZE = 8 * DIARY_DONUT_SCALE;

interface ReportDetailModalProps {
  selectedReport: Report | null;
  isPlus: boolean;
  onUpgradeClick: () => void;
  dailyMoodDistribution: MoodDistributionItem[];
  onClose: () => void;
  onBack?: () => void;
  onOpenPlantCard?: (plant: DailyPlantRecord) => void;
  onShowTaskList: (type: 'completed' | 'total') => void;
  generateAIDiary: (reportId: string) => Promise<void>;
  initialPage?: 0 | 1;
  presentation?: 'modal' | 'page';
  onOpenCalendar?: () => void;
  onOpenDiaryBook?: () => void;
  readOnly?: boolean;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  canNavigateNext?: boolean;
}

type Lang = 'zh' | 'en' | 'it';
const COPY: Record<Lang, {
  pageTitle: string;
  sectionActivity: string;
  sectionMood: string;
  sectionTodo: string;
  sectionHabits: string;
  sectionObservation: string;
  sectionMyDiary: string;
  activityLine1: string;
  moodLine1: string;
  todoFallback: string;
  habitsFallback: string;
  diaryPlaceholder: string;
}> = {
  zh: {
    pageTitle: '日记',
    sectionActivity: '活动',
    sectionMood: '情绪',
    sectionTodo: '待办',
    sectionHabits: '习惯',
    sectionObservation: '观察日记',
    sectionMyDiary: '我的日记',
    activityLine1: '今天主要精力投入在工作上',
    moodLine1: '整体情绪比较轻松愉快',
    todoFallback: '你今天做得很好',
    habitsFallback: '继续保持，进度很稳',
    diaryPlaceholder: '今天还没有写下内容。',
  },
  en: {
    pageTitle: 'diary',
    sectionActivity: 'activity',
    sectionMood: 'mood',
    sectionTodo: 'to-do',
    sectionHabits: 'habits',
    sectionObservation: 'observation',
    sectionMyDiary: 'my diary',
    activityLine1: 'Mostly working today',
    moodLine1: 'Feeling joyful most of the day',
    todoFallback: 'You did great today',
    habitsFallback: 'Nice rhythm, keep going',
    diaryPlaceholder: 'No diary content yet.',
  },
  it: {
    pageTitle: 'diario',
    sectionActivity: 'attivita',
    sectionMood: 'umore',
    sectionTodo: 'to-do',
    sectionHabits: 'abitudini',
    sectionObservation: 'osservazione',
    sectionMyDiary: 'il mio diario',
    activityLine1: 'Oggi soprattutto lavoro',
    moodLine1: 'Umore positivo per gran parte della giornata',
    todoFallback: 'Hai fatto un ottimo lavoro oggi',
    habitsFallback: 'Continua cosi, ottimo passo',
    diaryPlaceholder: 'Nessun contenuto del diario per ora.',
  },
};


function isLikelyLegacyClampedSummary(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  const chars = Array.from(trimmed);
  if (chars.length < 50) return false;
  const tail = chars[chars.length - 1] || '';
  return !/[。！？.!?…]$/.test(tail);
}

function containsHanText(text: string): boolean {
  return /[\u4E00-\u9FFF]/.test(text);
}

function shouldUseStoredLocalizedSummary(text: string, lang: Lang): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (isLikelyLegacyClampedSummary(trimmed)) return false;
  if (lang !== 'zh' && containsHanText(trimmed)) return false;
  if (lang === 'zh' && !containsHanText(trimmed)) return false;
  return true;
}

function WaveDivider() {
  return (
    <div
      style={{
        flexShrink: 0,
        height: '1px',
        backgroundImage: 'repeating-linear-gradient(90deg, #D0D0D0 0, #D0D0D0 3px, transparent 0, transparent 7px)',
        margin: '2px 0',
      }}
    />
  );
}

function SectionRow({ left, lines }: { left: React.ReactNode; lines: string[] }) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 8px 1fr' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>{left}</div>
      <div />
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
        {lines.map((line, index) => (
          <div
            key={index}
            className={index === 0 ? 'app-description' : 'app-caption'}
            style={{
              color: index === 0 ? '#1A1A1A' : '#737373',
              whiteSpace: index === 0 ? 'normal' : 'nowrap',
              overflowWrap: 'break-word',
              overflow: index === 0 ? 'visible' : 'hidden',
              textOverflow: index === 0 ? undefined : 'ellipsis',
            }}
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  );
}

export const ReportDetailModal: React.FC<ReportDetailModalProps> = ({
  selectedReport,
  isPlus,
  onUpgradeClick,
  dailyMoodDistribution: _dailyMoodDistribution,
  onClose,
  onBack,
  onOpenPlantCard,
  onShowTaskList: _onShowTaskList,
  generateAIDiary: _generateAIDiary,
  initialPage,
  presentation = 'modal',
  onOpenCalendar,
  onOpenDiaryBook,
  readOnly: _readOnly,
  onNavigatePrev,
  onNavigateNext,
  canNavigateNext,
}) => {
  const { t, i18n } = useTranslation();
  const chatMessages = useChatStore((state) => state.messages);
  const dateCache = useChatStore((state) => state.dateCache);
  const activityMood = useMoodStore((state) => state.activityMood);
  const customMoodLabel = useMoodStore((state) => state.customMoodLabel);
  const customMoodApplied = useMoodStore((state) => state.customMoodApplied);
  const todos = useTodoStore((state) => state.todos);
  const bottles = useGrowthStore((state) => state.bottles);
  const aiMode = useAuthStore((state) => state.preferences.aiMode);
  const userId = useAuthStore((state) => state.user?.id);
  const selectedReportDay = selectedReport?.date
    ? format(new Date(selectedReport.date), 'yyyy-MM-dd')
    : null;
  const todayPlant = usePlantStore((state) => state.todayPlant);
  const historyUserId = usePlantStore((state) => state.historyUserId);
  const historyPlantsByDate = usePlantStore((state) => state.historyPlantsByDate);
  const loadPlantHistory = usePlantStore((state) => state.loadPlantHistory);
  const cachePlantRecord = usePlantStore((state) => state.cachePlantRecord);
  const ensureDiaryPageSnapshot = useReportStore((state) => state.ensureDiaryPageSnapshot);
  const updateReport = useReportStore((state) => state.updateReport);
  const currentDay = format(new Date(), 'yyyy-MM-dd');
  const pagesRef = useRef<HTMLDivElement | null>(null);
  const [activePage, setActivePage] = useState(initialPage ?? 0);
  const [diaryActionHint, setDiaryActionHint] = useState<string | null>(null);
  const [isDiaryGenerating, setIsDiaryGenerating] = useState(false);
  const [editableMyDiaryText, setEditableMyDiaryText] = useState('');
  const [isMyDiaryEditing, setIsMyDiaryEditing] = useState(false);
  const [isMyDiarySaving, setIsMyDiarySaving] = useState(false);
  const plantAutoAttemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const page = initialPage ?? 0;
    setActivePage(page);
    requestAnimationFrame(() => {
      if (!pagesRef.current) return;
      pagesRef.current.scrollLeft = page === 1 ? pagesRef.current.clientWidth : 0;
    });
  }, [selectedReport?.id, initialPage]);

  const lang = useMemo<Lang>(() => {
    const raw = i18n.language?.split('-')[0] ?? 'en';
    if (raw === 'zh' || raw === 'it') return raw;
    return 'en';
  }, [i18n.language]);

  const copy = COPY[lang];
  const visibleDayPlant = selectedReportDay
    ? resolveCachedPlantForDate(
      { historyUserId, historyPlantsByDate, todayPlant },
      userId,
      selectedReportDay,
      currentDay,
    )
    : null;
  const dayPlantName = visibleDayPlant ? getPlantDisplayName(visibleDayPlant.plantId, lang) : '';
  const diaryAlreadyGenerated = useMemo(() => {
    if (!selectedReport) return false;
    const fullText = selectedReport.aiAnalysis?.trim();
    const teaserText = selectedReport.teaserText?.trim();
    return isPlus ? Boolean(fullText) : Boolean(teaserText);
  }, [isPlus, selectedReport]);
  const diaryPageSnapshot = useMemo(() => {
    if (!selectedReport?.stats || !diaryAlreadyGenerated) return null;
    return selectedReport.stats.diaryPageSnapshot
      ?? buildDiaryPageSnapshot(selectedReport.stats, lang as DiarySnapshotLang);
  }, [diaryAlreadyGenerated, lang, selectedReport?.stats]);

  useEffect(() => {
    if (
      !selectedReport
      || !diaryAlreadyGenerated
      || selectedReport.stats?.diaryPageSnapshot?.version === 2
    ) return;
    ensureDiaryPageSnapshot(selectedReport.id);
  }, [
    diaryAlreadyGenerated,
    ensureDiaryPageSnapshot,
    selectedReport?.id,
    selectedReport?.stats?.diaryPageSnapshot,
  ]);

  const reportMessages = useMemo(
    () => getMessagesForReport(chatMessages, dateCache, selectedReport),
    [chatMessages, dateCache, selectedReport],
  );
  const activityDistribution = useMemo(
    () => diaryPageSnapshot
      ? diaryPageSnapshot.actionAnalysis.map(item => ({ type: item.category, minutes: item.minutes }))
      : getDailyActivityDistribution(reportMessages, selectedReport),
    [diaryPageSnapshot, reportMessages, selectedReport],
  );
  const moodDistribution = useMemo(
    () => diaryPageSnapshot?.moodDistribution
      ?? getDailyMoodDistribution(reportMessages, { activityMood, customMoodLabel, customMoodApplied }, selectedReport),
    [diaryPageSnapshot, reportMessages, activityMood, customMoodLabel, customMoodApplied, selectedReport],
  );
  const isTodayReport = useMemo(
    () => Boolean(selectedReport?.date && isSameDay(new Date(selectedReport.date), new Date())),
    [selectedReport?.date],
  );
  const diaryCanGenerate = useMemo(() => {
    if (!isTodayReport) return false;
    if (new Date().getHours() < 20) return false;
    if (diaryAlreadyGenerated) return false;
    if (isDiaryGenerating || selectedReport?.analysisStatus === 'generating') return false;
    return true;
  }, [diaryAlreadyGenerated, isDiaryGenerating, isTodayReport, selectedReport?.analysisStatus]);
  const isMyDiaryEditable = useMemo(() => {
    if (!selectedReport || _readOnly) return false;
    return selectedReport.type === 'daily' && canEditDailyMyDiary(selectedReport.date);
  }, [_readOnly, selectedReport]);

  useEffect(() => {
    setEditableMyDiaryText(selectedReport?.userNote ?? '');
    setIsMyDiaryEditing(false);
    setIsMyDiarySaving(false);
  }, [selectedReport?.id, selectedReport?.userNote]);

  const liveTodayTodoStats = useMemo(() => {
    if (diaryPageSnapshot || !isTodayReport || !selectedReport?.date) return null;
    const targetDate = new Date(selectedReport.date);
    const dailyStats = computeDailyTodoStats(
      todos,
      bottles.map((b) => ({ id: b.id, name: b.name, type: b.type, stars: b.stars })),
      startOfDay(targetDate),
      endOfDay(targetDate),
    );
    const oneTimeTotal = dailyStats.oneTimeTasks.high.total + dailyStats.oneTimeTasks.medium.total + dailyStats.oneTimeTasks.low.total;
    const oneTimeCompleted = dailyStats.oneTimeTasks.high.completed + dailyStats.oneTimeTasks.medium.completed + dailyStats.oneTimeTasks.low.completed;
    const total = oneTimeTotal + dailyStats.independentRecurring.total + dailyStats.habitCheckin.length + dailyStats.goalProgress.length;
    const completed = oneTimeCompleted + dailyStats.independentRecurring.completed + dailyStats.habitCheckin.filter((item) => item.done).length + dailyStats.goalProgress.filter((item) => item.doneToday).length;
    return {
      completed,
      total,
      oneTimeCompletedTitles: dailyStats.oneTimeTasks.completedTitles,
      habitDoneCount: dailyStats.habitCheckin.filter((item) => item.done).length,
      habitTotalCount: dailyStats.habitCheckin.length,
      goalDoneCount: dailyStats.goalProgress.filter((item) => item.doneToday).length,
      goalTotalCount: dailyStats.goalProgress.length,
    };
  }, [diaryPageSnapshot, isTodayReport, selectedReport?.date, todos, bottles]);

  const todoCompleted = diaryPageSnapshot?.todoCompleted
    ?? liveTodayTodoStats?.completed
    ?? selectedReport?.stats?.completedTodos
    ?? 0;
  const todoTotal = diaryPageSnapshot?.todoTotal
    ?? liveTodayTodoStats?.total
    ?? selectedReport?.stats?.totalTodos
    ?? 0;
  const todoCompletionRate = diaryPageSnapshot?.todoCompletionRate
    ?? (todoTotal > 0
      ? todoCompleted / todoTotal
      : Math.max(0, Math.min(1, selectedReport?.stats?.completionRate ?? 0)));
  const habitDoneCount = diaryPageSnapshot?.habitDone
    ?? liveTodayTodoStats?.habitDoneCount
    ?? selectedReport?.stats?.habitCheckin?.filter((item) => item.done).length
    ?? 0;
  const habitTotalCount = diaryPageSnapshot?.habitTotal
    ?? liveTodayTodoStats?.habitTotalCount
    ?? selectedReport?.stats?.habitCheckin?.length
    ?? 0;
  const goalDoneCount = diaryPageSnapshot?.goalDone
    ?? liveTodayTodoStats?.goalDoneCount
    ?? selectedReport?.stats?.goalProgress?.filter((item) => item.doneToday).length
    ?? 0;
  const goalTotalCount = diaryPageSnapshot?.goalTotal
    ?? liveTodayTodoStats?.goalTotalCount
    ?? selectedReport?.stats?.goalProgress?.length
    ?? 0;
  const todayStars = diaryPageSnapshot?.starsToday ?? habitDoneCount + goalDoneCount;

  const handleGenerateDiaryClick = useCallback(async () => {
    if (!selectedReport) return;
    if (diaryAlreadyGenerated) {
      setDiaryActionHint(t('report_generate_already'));
      return;
    }
    if (new Date().getHours() < 20) {
      setDiaryActionHint(t('report_early_tip'));
      return;
    }
    setDiaryActionHint(null);
    setIsDiaryGenerating(true);
    try {
      await _generateAIDiary(selectedReport.id);
    } finally {
      setIsDiaryGenerating(false);
    }
  }, [_generateAIDiary, diaryAlreadyGenerated, selectedReport, t]);

  const persistMyDiaryNote = useCallback(async () => {
    if (!selectedReport || !isMyDiaryEditable) return;
    if ((selectedReport.userNote ?? '') === editableMyDiaryText) return;
    await updateReport(selectedReport.id, { userNote: editableMyDiaryText });
  }, [editableMyDiaryText, isMyDiaryEditable, selectedReport, updateReport]);

  const handleMyDiarySave = useCallback(async () => {
    if (!isMyDiaryEditable) return;
    setIsMyDiarySaving(true);
    try {
      await persistMyDiaryNote();
    } finally {
      setIsMyDiarySaving(false);
      setIsMyDiaryEditing(false);
    }
  }, [isMyDiaryEditable, persistMyDiaryNote]);

  useEffect(() => {
    let cancelled = false;

    const date = selectedReport?.date;
    if (!date) return () => { cancelled = true; };

    const dayDate = new Date(date);
    const dayStr = format(dayDate, 'yyyy-MM-dd');
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const isPastDay = dayStr < todayStr;
    const langRaw = i18n.language?.toLowerCase() ?? 'en';
    const plantLang: 'zh' | 'en' | 'it' = langRaw.startsWith('zh')
      ? 'zh'
      : langRaw.startsWith('it')
        ? 'it'
        : 'en';

    void loadPlantHistory(dayStr, dayStr)
      .then(async (res) => {
        if (cancelled) return;
        if (res.length > 0) {
          const existingPlant = res[0];
          if (existingPlant.rootSnapshot) {
            return;
          }
          try {
            const snapshotted = await callPlantGenerateAPI({
              date: dayStr,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
              lang: plantLang,
              action: 'snapshot_existing',
            });
            if (!cancelled) {
              cachePlantRecord(snapshotted.plant ?? existingPlant, userId);
            }
          } catch {
            if (!cancelled) cachePlantRecord(existingPlant, userId);
          }
          return;
        }
        if (!isPastDay) return;
        if (plantAutoAttemptedRef.current.has(dayStr)) return;

        plantAutoAttemptedRef.current.add(dayStr);
        try {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
          const generated = await callPlantGenerateAPI({
            date: dayStr,
            timezone,
            lang: plantLang,
          });
          if (cancelled) return;
          if (generated.plant) {
            cachePlantRecord(generated.plant, userId);
            return;
          }
          if (generated.status === 'generated' || generated.status === 'already_generated') {
            const refreshed = await loadPlantHistory(dayStr, dayStr, true);
            if (!cancelled && refreshed.length > 0) {
              cachePlantRecord(refreshed[0], userId);
            }
          }
        } catch {
          // keep placeholder when generation fails or empty day
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    cachePlantRecord,
    i18n.language,
    loadPlantHistory,
    selectedReport?.date,
    selectedReport?.id,
    userId,
  ]);

  const dateLabel = useMemo(() => {
    const date = selectedReport?.date ? new Date(selectedReport.date) : new Date();
    const locale = lang === 'zh' ? zhCN : lang === 'it' ? itLocale : enUS;
    const pattern = lang === 'zh' ? 'yyyy年M月d日 EEEE' : 'EEEE, MMMM d, yyyy';
    return format(date, pattern, { locale });
  }, [selectedReport?.date, lang]);

  const onScroll = useCallback(() => {
    const el = pagesRef.current;
    if (!el) return;
    const next = Math.round(el.scrollLeft / el.clientWidth) >= 1 ? 1 : 0;
    setActivePage(next);
  }, []);

  const handleHeaderBack = useCallback(() => {
    if (onNavigatePrev) {
      onNavigatePrev();
      return;
    }
    (onBack ?? onClose)();
  }, [onNavigatePrev, onBack, onClose]);

  const observationText = resolveReportObservationText({
    report: selectedReport,
    isPlus,
    loadingText: t('report_generating_variant_1', {
      companion: getReportCompanionName(aiMode),
    }),
    fallbackText: DIARY_COPY[lang].observationFallback,
  });
  const shouldShowUpgradeMask = !isPlus && selectedReport?.analysisStatus !== 'generating';

  const myDiaryText = useMemo(() => {
    const raw = editableMyDiaryText.trim();
    return raw && raw.length > 0 ? raw : copy.diaryPlaceholder;
  }, [editableMyDiaryText, copy.diaryPlaceholder]);

  const activityChartData = useMemo<DataItem[]>(() => {
    if (activityDistribution.length === 0) return [{ name: t('no_data'), value: 100, color: '#E5E7EB' }];
    const top = activityDistribution.slice(0, 5);
    const total = top.reduce((sum, item) => sum + item.minutes, 0) || 1;
    const withPercent = top.map((item, index) => ({
      name: t(ACTIVITY_I18N_KEYS[item.type] || item.type).toLowerCase(),
      value: Math.max(1, Math.round((item.minutes / total) * 100)),
      color: ACTIVITY_UI_COLORS[index] || ACTIVITY_UI_COLORS[ACTIVITY_UI_COLORS.length - 1],
    }));
    const sumPercent = withPercent.reduce((sum, item) => sum + item.value, 0);
    if (sumPercent !== 100 && withPercent.length > 0) withPercent[0].value += (100 - sumPercent);
    return withPercent;
  }, [activityDistribution, t]);

  const moodChartData = useMemo<DataItem[]>(() => {
    if (moodDistribution.length === 0) return [{ name: t('no_data'), value: 100, color: '#E5E7EB' }];
    const top = moodDistribution.slice(0, 4);
    const total = top.reduce((sum, item) => sum + item.minutes, 0) || 1;
    const withPercent = top.map((item, index) => ({
      name: getMoodDisplayLabel(item.mood, t).toLowerCase(),
      value: Math.max(1, Math.round((item.minutes / total) * 100)),
      color: MOOD_UI_COLORS[index] || MOOD_UI_COLORS[MOOD_UI_COLORS.length - 1],
    }));
    const sumPercent = withPercent.reduce((sum, item) => sum + item.value, 0);
    if (sumPercent !== 100 && withPercent.length > 0) withPercent[0].value += (100 - sumPercent);
    return withPercent;
  }, [moodDistribution, t]);

  const actionSummaryText = useMemo(() => {
    if (diaryPageSnapshot) return diaryPageSnapshot.activitySummary;
    const stored = typeof selectedReport?.stats?.actionSummary === 'string'
      ? selectedReport.stats.actionSummary.trim()
      : '';
    if (shouldUseStoredLocalizedSummary(stored, lang)) return stored;

    const source = selectedReport?.stats?.actionAnalysis;
    if (!source || source.length === 0) return '';
    return generateActionSummary(source, lang);
  }, [diaryPageSnapshot, selectedReport?.stats?.actionSummary, selectedReport?.stats?.actionAnalysis, lang]);

  const moodSummaryText = useMemo(() => {
    if (diaryPageSnapshot) return diaryPageSnapshot.moodSummary;
    const stored = typeof selectedReport?.stats?.moodSummary === 'string'
      ? selectedReport.stats.moodSummary.trim()
      : '';
    if (shouldUseStoredLocalizedSummary(stored, lang)) return stored;

    const source = selectedReport?.stats?.moodDistribution;
    if (!source || source.length === 0) return '';
    return generateMoodSummary(source, lang);
  }, [diaryPageSnapshot, selectedReport?.stats?.moodSummary, selectedReport?.stats?.moodDistribution, lang]);

  const activityAnalysisLine1 = actionSummaryText || copy.activityLine1;
  const moodAnalysisLine1 = moodSummaryText || copy.moodLine1;
  const maxAct = activityChartData.reduce((m, c, i, arr) => (c.value > arr[m].value ? i : m), 0);
  const maxMood = moodChartData.reduce((m, c, i, arr) => (c.value > arr[m].value ? i : m), 0);
  const todoSegments = 12;
  const todoLitCount = todoTotal > 0
    ? Math.min(
      todoSegments,
      Math.max(todoCompletionRate > 0 ? 1 : 0, Math.round(todoCompletionRate * todoSegments)),
    )
    : 0;
  const todoAnalysisLine1 = diaryPageSnapshot?.todoSummary || copy.todoFallback;
  const todoAnalysisLine2 = todoTotal > 0 ? `${todoCompleted}/${todoTotal}` : '';
  const habitAnalysisLine1 = diaryPageSnapshot?.habitSummary || copy.habitsFallback;
  const habitAnalysisLine2 = t('diary_star_count', { count: todayStars });

  if (!selectedReport) return null;

  return (
    <div
      className={presentation === 'page' ? 'absolute inset-0 z-10' : 'fixed inset-0 z-[60]'}
      style={{
        background: '#FFFFFF',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: presentation === 'page' ? 0 : 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        ref={pagesRef}
        onScroll={onScroll}
        className="flex h-full overflow-x-scroll [&::-webkit-scrollbar]:hidden"
        style={{ scrollSnapType: 'x mandatory' }}
      >
          <div
            className="diary-page-entry-hint relative w-full h-full shrink-0 flex flex-col overflow-hidden"
            style={{ background: '#FFFFFF', scrollSnapAlign: 'start' }}
          >
            <ReportDetailPageHeader
              activePage={activePage}
              date={dateLabel}
              title={copy.pageTitle}
              onBack={handleHeaderBack}
              onClose={onClose}
              onOpenCalendar={onOpenCalendar}
              onOpenDiaryBook={onOpenDiaryBook}
              calendarLabel={t('report_calendar_view')}
              diaryBookLabel={t('report_view_diary_book')}
              isPrimaryPage={presentation === 'page'}
              onNextDate={onNavigateNext && canNavigateNext !== false ? onNavigateNext : undefined}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingBottom: '12px', paddingLeft: '16px', paddingRight: '16px', overflowY: 'auto', overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div style={{ flex: '1 0 138px', minHeight: '138px', display: 'flex', flexDirection: 'column' }}>
                  <div className="app-body font-bold" style={{ flexShrink: 0, alignSelf: 'flex-start', padding: '1px 6px' }}>{copy.sectionActivity}</div>
                  <SectionRow
                    left={<DonutChart
                      data={activityChartData}
                      maxIndex={maxAct}
                      chartId="diary-activity"
                      labelColor="#2D5A30"
                      size={DIARY_DONUT_SIZE}
                      innerRadius={DIARY_DONUT_INNER_RADIUS}
                      outerRadius={DIARY_DONUT_OUTER_RADIUS}
                      fontSize={DIARY_DONUT_FONT_SIZE}
                    />}
                    lines={[activityAnalysisLine1]}
                  />
                </div>

                <WaveDivider />

                <div style={{ flex: '1 0 138px', minHeight: '138px', display: 'flex', flexDirection: 'column' }}>
                  <div className="app-body font-bold" style={{ flexShrink: 0, alignSelf: 'flex-start', padding: '1px 6px' }}>{copy.sectionMood}</div>
                  <SectionRow
                    left={<DonutChart
                      data={moodChartData}
                      maxIndex={maxMood}
                      chartId="diary-mood"
                      labelColor="#A0304A"
                      size={DIARY_DONUT_SIZE}
                      innerRadius={DIARY_DONUT_INNER_RADIUS}
                      outerRadius={DIARY_DONUT_OUTER_RADIUS}
                      fontSize={DIARY_DONUT_FONT_SIZE}
                    />}
                    lines={[moodAnalysisLine1]}
                  />
                </div>

                <WaveDivider />

                <div style={{ flex: '1 0 138px', minHeight: '138px', display: 'flex', flexDirection: 'column' }}>
                  <div className="app-body font-bold" style={{ flexShrink: 0, alignSelf: 'flex-start', padding: '1px 6px' }}>{copy.sectionTodo}</div>
                  <SectionRow
                    left={(
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {Array.from({ length: todoSegments }).map((_, index) => (
                          <div key={index} style={{ width: '6px', height: '3px', borderRadius: '1px', backgroundColor: index < todoLitCount ? '#F5C842' : '#EDE0B0' }} />
                        ))}
                      </div>
                    )}
                    lines={[todoAnalysisLine1, todoAnalysisLine2].filter(Boolean)}
                  />
                </div>

                <WaveDivider />

                <div style={{ flex: '1 0 138px', minHeight: '138px', display: 'flex', flexDirection: 'column' }}>
                  <div className="app-body font-bold" style={{ flexShrink: 0, alignSelf: 'flex-start', padding: '1px 6px' }}>{copy.sectionHabits}</div>
                  <SectionRow
                    left={(
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                        {[0, 1].map((row) => (
                          <div key={row} style={{ display: 'flex', gap: '4px' }}>
                            {Array.from({ length: 5 }).map((_, col) => {
                              const index = row * 5 + col;
                              return (
                                <img
                                  key={col}
                                  src={growthStarImage}
                                  alt="star"
                                  style={{ width: '20px', height: '20px', objectFit: 'contain', opacity: index < todayStars ? 1 : 0.25 }}
                                />
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                    lines={[habitAnalysisLine1, habitAnalysisLine2].filter(Boolean)}
                  />
                </div>
              </div>

              <div style={{ flexShrink: 0, borderTop: '0.5px solid #D0D0D0', marginTop: '4px' }} />
            </div>
          </div>

          <div className="diary-page-entry-hint relative w-full h-full shrink-0 flex flex-col overflow-hidden" style={{ background: '#FFFFFF', scrollSnapAlign: 'start' }}>
            <ReportDetailPageHeader
              activePage={activePage}
              date={dateLabel}
              title={copy.pageTitle}
              onBack={handleHeaderBack}
              onClose={onClose}
              onOpenCalendar={onOpenCalendar}
              onOpenDiaryBook={onOpenDiaryBook}
              calendarLabel={t('report_calendar_view')}
              diaryBookLabel={t('report_view_diary_book')}
              isPrimaryPage={presentation === 'page'}
              onNextDate={onNavigateNext && canNavigateNext !== false ? onNavigateNext : undefined}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingBottom: '12px', paddingLeft: '16px', paddingRight: '16px', overflow: 'hidden', minHeight: 0 }}>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div style={{ flex: 2, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <div className="app-body font-bold" style={{ flexShrink: 0, padding: '1px 0' }}>{copy.sectionObservation}</div>

                  <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', paddingRight: 8 }}>
                    <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>
                      <div style={{ float: 'left', width: 150, marginRight: 8, background: '#FFFFFF' }}>
                        {visibleDayPlant ? (
                          <button
                            type="button"
                            onClick={() => onOpenPlantCard?.(visibleDayPlant)}
                            style={{ width: '100%', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
                          >
                            <PlantImage
                              plantId={visibleDayPlant.plantId}
                              rootType={visibleDayPlant.rootType}
                              plantStage={visibleDayPlant.plantStage}
                              imgClassName="w-full h-auto"
                            />
                            {dayPlantName ? (
                              <span
                                style={{
                                  display: 'block',
                                  marginTop: 3,
                                  color: 'rgba(26,26,26,0.58)',
                                  fontSize: 10,
                                  lineHeight: 1.35,
                                  textAlign: 'center',
                                }}
                              >
                                {dayPlantName}
                              </span>
                            ) : null}
                          </button>
                        ) : (
                          <div style={{ width: '100%', height: 150 }} />
                        )}
                      </div>
                      {!shouldShowUpgradeMask ? (
                        <p className="app-description" style={{ margin: 0, padding: 0, color: '#1A1A1A', wordBreak: 'normal', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                          {observationText}
                        </p>
                      ) : (
                        <div style={{ position: 'relative', minHeight: 180 }}>
                          <p className="app-description" style={{ margin: 0, padding: 0, color: '#1A1A1A', wordBreak: 'normal', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                            {observationText}
                          </p>
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'linear-gradient(180deg, rgba(255,255,255,0) 35%, rgba(255,255,255,0.85) 65%, rgba(255,255,255,1) 100%)',
                              display: 'flex',
                              alignItems: 'flex-end',
                              justifyContent: 'center',
                              paddingBottom: 8,
                            }}
                          >
                            <button
                              type="button"
                              onClick={onUpgradeClick}
                              className="app-caption font-bold"
                            style={{
                                border: 'none',
                                borderRadius: 999,
                                background: '#4f46e5',
                                color: '#fff',
                                padding: '6px 12px',
                              }}
                            >
                              {t('report_teaser_unlock')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <WaveDivider />

                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <div className="app-body font-bold" style={{ flexShrink: 0, padding: '1px 0' }}>{copy.sectionMyDiary}</div>
                  <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
                    <textarea
                      readOnly={!isMyDiaryEditable}
                      value={isMyDiaryEditable ? editableMyDiaryText : myDiaryText}
                      onChange={(event) => {
                        if (!isMyDiaryEditable) return;
                        setEditableMyDiaryText(event.target.value);
                      }}
                      onFocus={() => {
                        if (!isMyDiaryEditable) return;
                        setIsMyDiaryEditing(true);
                      }}
                      onBlur={() => {
                        if (!isMyDiaryEditable || !isMyDiaryEditing) return;
                        void persistMyDiaryNote();
                        setIsMyDiaryEditing(false);
                      }}
                      className="app-form-text"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        outline: 'none',
                        resize: 'none',
                        background: 'transparent',
                        lineHeight: '18px',
                        color: myDiaryText === copy.diaryPlaceholder ? '#C8C8C0' : '#1A1A1A',
                        padding: '0 4px 0 2px',
                        margin: 0,
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    />
                    {isMyDiaryEditable && isMyDiaryEditing ? (
                      <div style={{ position: 'absolute', right: 0, bottom: 0, paddingBottom: 2 }}>
                        <button
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => { void handleMyDiarySave(); }}
                          disabled={isMyDiarySaving}
                          className="app-caption rounded-full px-3 py-1 font-semibold disabled:opacity-70"
                          style={APP_GREEN_GLASS_BUTTON_STYLE}
                        >
                          {isMyDiarySaving ? `${t('report_save')}...` : t('report_save')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div style={{ flexShrink: 0, borderTop: '0.5px solid #D0D0D0', marginTop: '4px' }} />
                      </div>
                    </div>
                  </div>

                  {isTodayReport ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, paddingTop: 8 }}>
                      <button
                        type="button"
                        onClick={() => { void handleGenerateDiaryClick(); }}
                        disabled={!diaryCanGenerate}
                        className="app-body rounded-full px-5 py-1.5 font-medium transition active:opacity-70 disabled:opacity-55 disabled:cursor-not-allowed"
                        style={APP_GREEN_GLASS_BUTTON_STYLE}
                      >
                        {isDiaryGenerating ? t('report_generating', { companion: getReportCompanionName(aiMode) }) : t('report_generate_button')}
                      </button>
                      {diaryActionHint ? (
                        <p className="app-badge text-center" style={{ color: '#5f6f65', margin: 0 }}>
                          {diaryActionHint}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
  );
};
